import axios from 'axios';

import {ToastService} from '@iamsoftware/react-hooks';

import {LoadingService} from '../AppLoading';

import {baseURL} from '../config';

import {AuthService} from './AuthService';

import {AppStorage} from './UserStorage';

const _axios = axios.create({
  baseURL
});

const getExpireTimeMs = (expireStr: string): number => {
  if (!expireStr) return 0;
  const asNum = Number(expireStr);
  if (!isNaN(asNum) && asNum > 0) {
    return asNum;
  }
  const asDate = Date.parse(expireStr);
  return isNaN(asDate) ? 0 : asDate;
};

let refreshPromise: Promise<string> | null = null;

const doRefreshToken = async (refreshToken: string): Promise<string> => {
  try {
    const res = await axios.post(`${baseURL}/iam-sso/keycloak-oidc/refresh-token`, { refreshToken });
    const data = res.data;
    if (data && (data.isSuccess === 'Y' || data.success === 'Y' || data.accessToken)) {
      if (data.accessToken) AppStorage.set('accessToken', data.accessToken);
      if (data.refreshToken) AppStorage.set('refreshToken', data.refreshToken);
      if (data.accessTokenExpireAt) AppStorage.set('accessTokenExpireAt', String(data.accessTokenExpireAt));
      if (data.refreshTokenExpireAt) AppStorage.set('refreshTokenExpireAt', String(data.refreshTokenExpireAt));
      if (data.moquiSessionToken) AppStorage.set('moquiSessionToken', data.moquiSessionToken);
      return data.accessToken;
    }
    throw new Error('Refresh token response invalid');
  } catch (err) {
    AppStorage.remove('accessToken');
    AppStorage.remove('refreshToken');
    window.location.hash = '/login';
    throw err;
  } finally {
    refreshPromise = null;
  }
};

_axios.interceptors.request.use(async config => {
  if (!(config as any).skipLoading) {
    LoadingService.start();
  }

  const isAuthRequest = config.url?.includes('refresh-token') || config.url?.includes('login') || config.url?.includes('exchange-code');
  const refreshToken = AppStorage.get('refreshToken');
  const expireStr = AppStorage.get('accessTokenExpireAt');

  if (!isAuthRequest && refreshToken && expireStr) {
    const expireTimeMs = getExpireTimeMs(expireStr);
    if (expireTimeMs > 0 && expireTimeMs - Date.now() < 600000) {
      if (!refreshPromise) {
        refreshPromise = doRefreshToken(refreshToken);
      }
      try {
        await refreshPromise;
      } catch (e) {
      }
    }
  }

  config.headers['X-CSRF-Token'] = AppStorage.get('moquiSessionToken');

  const accessToken = AppStorage.get('accessToken');
  if (accessToken) {
    config.headers['sso_access_token'] = accessToken;
  }

  return config;
}, error => {
  return Promise.reject(error);
});
_axios.interceptors.response.use(response => {
  if (!(response.config as any).skipLoading) {
    LoadingService.stop();
  }
  return response;
}, async error => {

  const originalConfig = error.config;

  if (!originalConfig?.skipLoading) {
    LoadingService.stop();
  }

  if (error && error.response && error.response.status === 401 && !originalConfig?._retry) {
    originalConfig._retry = true;
    const refreshToken = AppStorage.get('refreshToken');
    if (refreshToken) {
      if (!refreshPromise) {
        refreshPromise = doRefreshToken(refreshToken);
      }
      try {
        const newAccessToken = await refreshPromise;
        if (newAccessToken) {
          originalConfig.headers['sso_access_token'] = newAccessToken;
          originalConfig.headers['X-CSRF-Token'] = AppStorage.get('moquiSessionToken');
          return _axios(originalConfig);
        }
      } catch (refreshErr) {
        AuthService.getToken().then(() => {
          window.location.hash = '/login';
        });
        return Promise.reject(refreshErr);
      }
    } else {
      AuthService.getToken().then(() => {
        window.location.hash = '/login';
      });
    }
  } else if (error.response && error.response.data && error.response.data.errors) {
    // Suppress global error popup per request: no Toast on non-401 errors
    // You can keep a console trace for debugging if needed
    // console.error('[API ERROR]', error.response.data.errors);
  }
  return Promise.reject(error);
});

export default _axios;
